import { CantileverButton } from 'components/CantileverButton'
import dayjs from 'dayjs'
import { TimeRange } from 'features/systemLens'
import {
  canNavigateForward,
  navigateTimeRange,
  TimeSeriesService,
} from 'features/timeSeries'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import {
  ChevronLeft,
  ChevronRight,
  RotateCw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { ChartTimeUnit } from 'types'
import { updateColorOpacity } from 'utils/colorHelpers'

import { ActiveTimeRangeSectionWithPopover } from './ActiveTimeRangeSectionWithPopover'

const ContainerQueryWrapper = styled.div`
  container-type: inline-size;
`

const ActiveRangeDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  padding: 0.25rem;
  border-radius: 0.5rem;

  div[data-role='back-forward'] {
    order: 0;
  }

  div[data-role='time-display'] {
    order: 1;
    padding: 0 0.25rem;
    border-radius: 0.25rem;
  }

  div[data-role='zoom'] {
    order: 2;
  }

  /* Middle states: group nav and zoom controls together below date */
  @container (max-width: 400px) {
    justify-content: space-between;

    div[data-role='time-display'] {
      order: 0;
      width: 100%;
      justify-content: space-between;
    }

    div[data-role='back-forward'] {
      order: 1;
    }

    div[data-role='zoom'] {
      order: 2;
    }
  }

  /* Narrowest state: date wraps, nav and zoom stay together */
  @container (max-width: 200px) {
    justify-content: flex-start;

    div[data-role='time-display'] {
      order: 0;
      width: 100%;
    }

    div[data-role='back-forward'] {
      order: 1;
    }

    div[data-role='zoom'] {
      order: 2;
    }
  }
`

const TimeDisplayContainer = styled.div`
  display: flex;
  @container (max-width: 400px) {
    width: 100%;
  }
`

const StyledIcon = css`
  width: 1rem;
  height: 1rem;
`

const StyledZoomIn = styled(ZoomIn)`
  ${StyledIcon}
`
const StyledZoomOut = styled(ZoomOut)`
  ${StyledIcon}
`
const StyledChevronLeft = styled(ChevronLeft)`
  ${StyledIcon}
`
const StyledChevronRight = styled(ChevronRight)`
  ${StyledIcon}
`
const RefreshIcon = styled(RotateCw)`
  width: 0.9rem;
  height: 0.9rem;
`

const RotatingContainer = styled.div<{ $isRotating: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  will-change: transform;
  backface-visibility: hidden;
  animation: ${({ $isRotating }) =>
    $isRotating ? 'rotate 1s ease-out forwards' : 'none'};
  animation-fill-mode: forwards;

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const RefreshButtonContainer = styled.div<{ $showIndicator?: boolean }>`
  button {
    background-color: ${({ $showIndicator, theme }) =>
      $showIndicator && theme.forecast.primary
        ? updateColorOpacity(theme.forecast.primary, 0.35)
        : 'transparent'};
  }
`

const ButtonsContainer = styled.div`
  display: flex;
`

interface ActiveTimeRangeSelectorProps {
  timeRange: TimeRange
  setTimeRange: React.Dispatch<React.SetStateAction<TimeRange>>
  onClickBackward?: () => void
  onClickForward?: () => void
  liveTail?: boolean
  onRangeClick?: () => void
}

export const ActiveTimeRangeSelector: React.FC<
  ActiveTimeRangeSelectorProps
> = ({
  timeRange,
  setTimeRange,
  onClickBackward,
  onClickForward,
  liveTail,
  onRangeClick,
}) => {
  const { setDateMode } = useScatterplotContext()
  const [intervalValue, setIntervalValue] = useState<number>(15)
  const [intervalUnit, setIntervalUnit] = useState<ChartTimeUnit>(
    ChartTimeUnit.MINUTES
  )
  const [showDateConfig, setShowDateConfig] = useState(false)
  const { fromTime, toTime } = useMemo(() => {
    return { fromTime: dayjs(timeRange.from), toTime: dayjs(timeRange.to) }
  }, [timeRange.from, timeRange.to])

  // Track previous timeRange.from to detect live tail refreshes
  const prevTimeRangeFromRef = React.useRef<string>(timeRange.from)
  const [isPeriodicRefresh, setIsPeriodicRefresh] = useState(false)

  // Detect when only timeRange.to changes in live tail mode (periodic refresh)
  useEffect(() => {
    if (liveTail && timeRange.from === prevTimeRangeFromRef.current) {
      // Only timeRange.to changed, this is a periodic refresh
      setIsPeriodicRefresh(true)
      // Animation duration is 1s (1 full rotation), let it complete before stopping
      const timer = setTimeout(() => {
        setIsPeriodicRefresh(false)
      }, 1000) // Match the animation duration so it finishes gracefully
      return () => clearTimeout(timer)
    } else if (!liveTail) {
      // Not in live tail mode, stop rotation
      setIsPeriodicRefresh(false)
    }
    // If timeRange.from changed, it's not a periodic refresh
  }, [liveTail, timeRange.from, timeRange.to])

  // Update ref when timeRange.from changes
  useEffect(() => {
    prevTimeRangeFromRef.current = timeRange.from
  }, [timeRange.from])

  // Calculate recommended interval based on time range
  useEffect(() => {
    const timeRangeSeconds = toTime.diff(fromTime, 'seconds')
    const { interval, interval_unit } =
      TimeSeriesService.getInterval(timeRangeSeconds)

    // Only update if not already set in timeRange
    if (!timeRange.interval || !timeRange.intervalUnit) {
      setIntervalValue(interval)
      setIntervalUnit(interval_unit)
      // Update the timeRange with calculated interval
      setTimeRange({
        ...timeRange,
        interval,
        intervalUnit: interval_unit,
      })
    } else {
      setIntervalValue(timeRange.interval)
      setIntervalUnit(timeRange.intervalUnit)
    }
  }, [setTimeRange, timeRange, fromTime, toTime])

  const handleBackwardClick = useCallback(() => {
    setDateMode('absolute')
    navigateTimeRange(
      'backward',
      false,
      fromTime,
      toTime,
      setTimeRange,
      intervalValue,
      intervalUnit
    )
    onClickBackward?.()
  }, [
    fromTime,
    toTime,
    setTimeRange,
    intervalValue,
    intervalUnit,
    onClickBackward,
    setDateMode,
  ])

  const handleForwardClick = useCallback(() => {
    setDateMode('absolute')
    navigateTimeRange(
      'forward',
      false,
      fromTime,
      toTime,
      setTimeRange,
      intervalValue,
      intervalUnit
    )
    onClickForward?.()
  }, [
    fromTime,
    toTime,
    setTimeRange,
    intervalValue,
    intervalUnit,
    onClickForward,
    setDateMode,
  ])

  const handleRangeClick = useCallback(() => {
    if (onRangeClick) {
      onRangeClick()
    } else {
      setShowDateConfig(!showDateConfig)
    }
  }, [onRangeClick, showDateConfig])

  const handlePopoverOpenChange = useCallback((open: boolean) => {
    setShowDateConfig(open)
  }, [])

  const handleRefreshClick = useCallback(() => {
    setTimeRange(timeRange)
  }, [setTimeRange, timeRange])

  const handleZoomOutClick = useCallback(() => {
    setDateMode('absolute')
    navigateTimeRange(
      'backward',
      true,
      fromTime,
      toTime,
      setTimeRange,
      intervalValue,
      intervalUnit
    )
  }, [fromTime, toTime, setTimeRange, intervalValue, intervalUnit, setDateMode])

  const handleZoomInClick = useCallback(() => {
    setDateMode('absolute')
    navigateTimeRange(
      'forward',
      true,
      fromTime,
      toTime,
      setTimeRange,
      intervalValue,
      intervalUnit
    )
  }, [fromTime, toTime, setTimeRange, intervalValue, intervalUnit, setDateMode])

  const isCustom = timeRange.preset === 'custom' && liveTail !== true

  return (
    <>
      <ContainerQueryWrapper>
        <ActiveRangeDisplayContainer>
          <ButtonsContainer data-role='back-forward'>
            <CantileverButton
              onClick={handleBackwardClick}
              ghost
              iconOnly
              title='Previous time period'>
              <StyledChevronLeft />
            </CantileverButton>
            <CantileverButton
              onClick={handleForwardClick}
              ghost
              iconOnly
              disabled={!canNavigateForward(toTime)}
              title='Next time period'>
              <StyledChevronRight />
            </CantileverButton>
          </ButtonsContainer>

          <TimeDisplayContainer data-role='time-display'>
            <ActiveTimeRangeSectionWithPopover
              isCustom={isCustom}
              onClick={handleRangeClick}
              fromTime={fromTime}
              toTime={toTime}
              timeRange={timeRange}
              onRangeClick={onRangeClick}
              showDateConfig={showDateConfig}
              onOpenChange={handlePopoverOpenChange}
              onClose={() => setShowDateConfig(false)}
            />
            <RefreshButtonContainer $showIndicator={isCustom}>
              <CantileverButton ghost iconOnly onClick={handleRefreshClick}>
                <RotatingContainer $isRotating={isPeriodicRefresh}>
                  <RefreshIcon />
                </RotatingContainer>
              </CantileverButton>
            </RefreshButtonContainer>
          </TimeDisplayContainer>

          <ButtonsContainer data-role='zoom'>
            <CantileverButton
              title='Zoom Out'
              ghost
              iconOnly
              onClick={handleZoomOutClick}>
              <StyledZoomOut />
            </CantileverButton>
            <CantileverButton
              title='Zoom In'
              ghost
              iconOnly
              onClick={handleZoomInClick}>
              <StyledZoomIn />
            </CantileverButton>
          </ButtonsContainer>
        </ActiveRangeDisplayContainer>
      </ContainerQueryWrapper>
    </>
  )
}
